import React from "react"

import { 
    Form, 
    Col, 
    Table, 
} from "react-bootstrap"

import EMETLAssessmentManager from "../../../Model/EMETLAssessment.m"
import EMETLSegmentedControl from "../EMETLSegmentedControl"

import "./style.css"

/**
 * Selector for the 'rating' of the assessment.
 *
 * @function
 * @param {object} props - Component props
 * @prop {number} selectedTPU - Index of the selected 'rating'
 * @prop {Function} selectTPU - Function to select the 'rating'
 * @prop {boolean} compact - Whether the component should be rendered in its 'compact' form
 * @returns {object} - React component
 */
export function TPUSelector(props)
{
    const { selectedTPU, selectTPU, compact } = props

    if (compact) {
        return (
            <Col className="float-center text-center align-middle align-items-center">
                <span
                    className={"rounded border border-5 border-info align-middle "
                    + "text-center font-weight-bold text-info"}
                    style={{
                        width: "60px", height: "24px", fontSize: "21px",
                        padding: "6px", paddingLeft: "9px", paddingRight: "9px",
                    }}
                >
                    { EMETLAssessmentManager.TASK_RATINGS[selectedTPU] }
                </span>
            </Col>
        )
    }

    return (
        <Col>
            <Form.Group>
                <EMETLSegmentedControl 
                    wrapperClass="taskCellSCWrapper float-right pr-10"
                    options={EMETLAssessmentManager.TASK_RATINGS} 
                    labelName="tpuSegmentedControl" 
                    perform={selectTPU}
                    selectedTab={selectedTPU}
                />
            </Form.Group>
        </Col>
    )
}

/**
 * Selector for the priority of the assessment.
 *
 * @function
 * @param {object} props - Component props
 * @prop {number} selectedPriority - Index of the selected priority
 * @prop {Function} selectPriority - Function to select the priority
 * @prop {boolean} compact - Whether the component should be rendered in its 'compact' form
 * @returns {object} - React component
 */
export function PrioritySelector(props)
{
    const { selectedPriority, selectPriority, compact } = props

    if (compact) {
        return (
            <Col className="float-center text-center align-middle align-items-center">
                <span
                    className={"rounded border border-5 border-success align-middle "
                    + "text-center font-weight-bold text-success"}
                    style={{
                        width: "60px", height: "24px", fontSize: "21px",
                        padding: "6px", paddingLeft: "9px", paddingRight: "9px",
                    }}
                >
                    { selectedPriority + 1 }
                </span>
            </Col>
        )
    }

    return (
        <>
            <Col>
                <Form.Label>Training Priority</Form.Label>
            </Col>
            <Col xs={10}>
                <EMETLSegmentedControl 
                    wrapperClass="taskCellSCWrapper" 
                    options={["1", "2", "3"]} 
                    labelName="prioritySegmentedControl" 
                    perform={selectPriority}
                    selectedTab={selectedPriority}
                />
            </Col>
        </>
    )
}

/**
 * Selector for the 'training type' of the assessment.
 *
 * @function
 * @param {object} props - Component props
 * @prop {number} selectedTrainingType - Index of the selected 'training type'
 * @prop {Function} selectTrainingType - Function to select the 'training type'
 * @prop {boolean} compact - Whether the component should be rendered in its 'compact' form
 * @returns {object} - React component
 */
export function NextActionSelector(props)
{
    const { selectedTrainingType, selectTrainingType, compact } = props
    let label = <Col><Form.Label>Next Quarter Action</Form.Label></Col>

    if (compact) label = <div />

    return (
        <>
            { label }
            <Col xs={10}>
                <EMETLSegmentedControl 
                    wrapperClass="taskCellSCWrapper" 
                    options={EMETLAssessmentManager.TRAINING_TYPES} 
                    labelName="trainingTypeSegmentedControl" 
                    perform={selectTrainingType}
                    selectedTab={selectedTrainingType}
                />
            </Col>
        </>
    )
}

/**
 * Text box to edit the 'specific needs'.
 *
 * @function
 * @param {object} props - Component props
 * @prop {string} currentNeeds - Text of the 'specific needs'
 * @prop {Function} setNeeds - Function to set the 'specific needs'
 * @returns {object} - React component
 */
export function NeedsEditor(props)
{
    const { currentNeeds, setNeeds } = props
    return (
        <div
            className="emetlTaskCellComponents"
        >
            <Col>
                <Form.Label>Specific Needs:</Form.Label>
            </Col>
            <Col xs={10}>
                <Form.Control
                    as="textarea"
                    // className={props.isTaskNeed ? "task-need" : ""}
                    className="needsEditor"
                    maxLength={1000}
                    value={currentNeeds}
                    placeholder="Enter Specific Needs"
                    onChange={({ target }) => setNeeds(target.value)}
                />
            </Col>
        </div>
    )
}

/**
 * Table of performance steps associated with the assessment.
 *
 * @function
 * @param {object} props - Component props
 * @prop {object} performanceSteps - List of performance step dictionaries
 * @prop {Function} setPerformanceStep - Function to update the performanceStep at an index
 * @returns {object} - React component
 */
export function PerformanceStepsTable(props)
{
    const { performanceSteps, setPerformanceStep } = props
    if (performanceSteps.length === 0) {
        return <div />
    }

    const rows = performanceSteps.map((perfStep, index) => (
        <PerformanceStepRow
            key={perfStep.ID}
            performanceStep={perfStep}
            updatePerformanceStep={(perfStep) => setPerformanceStep(index, perfStep)}
        />
    ))

    return (
        <Table striped bordered className="text-center">
            <thead>
                <th>Organization</th>
                <th>Number of Assessments</th>
                <th>Rating</th>
                <th>Needs</th>
            </thead>
            <tbody>
                { rows }
            </tbody>
        </Table>
    )
}

/**
 * A single row in the table of performance steps.
 *
 * @function
 * @param {object} props - Component props
 * @prop {object} performanceStep - Dictonary of the performance step this row represents
 * @prop {Function} updatePerformanceStep - Function to update the performance step
 * @returns {object} - React component
 */
function PerformanceStepRow(props)
{
    const { performanceStep, updatePerformanceStep } = props
    const {
        Rating: tpu,
        SpecificNeeds: needs,
        AssessmentCount: numAssessments,
        Organization: organization,
    } = performanceStep

    return (
        <tr>
            <td>{ organization }</td>
            <td>{ numAssessments }</td>
            <td>{ EMETLAssessmentManager.TASK_RATINGS[tpu] }</td>
            <td>
                <Form.Control
                    className="border-0"
                    type="text"
                    defaultValue={needs}
                    onChange={({ target: { value } }) => (
                        updatePerformanceStep({ ...performanceStep, SpecificNeeds: value })
                    )}
                />
            </td>
        </tr>
    )
}
